import { useState, useRef, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Plus, X } from 'lucide-react';
import { useProfile } from '@/lib/providers';

type PostIt = {
  id: string;
  text: string;
  color: string;
  position: { x: number; y: number };
  created_by: string;
};

const COLORS = [
  'bg-yellow-200',
  'bg-green-200',
  'bg-blue-200',
  'bg-pink-200',
  'bg-purple-200',
];

export function Whiteboard() {
  const { profile, loadingProfile } = useProfile();
  const [postIts, setPostIts] = useState<PostIt[]>([]);

  const [newPostIt, setNewPostIt] = useState('');
  const [selectedColor, setSelectedColor] = useState(COLORS[0]);
  const [draggedPostIt, setDraggedPostIt] = useState<string | null>(null);
  const whiteboardRef = useRef<HTMLDivElement>(null);

  const team = profile?.teams.at(profile.active_team_idx);

  useEffect(() => {
    if (!team) return;

    const loadNotes = async () => {
      const { data: notes } = await supabase
        .from('whiteboard_notes')
        .select('*')
        .eq('team_id', team.id);

      if (!notes) {
        setPostIts([]);
        return;
      }
      setPostIts(
        notes.map((note) => ({
          id: note.id,
          text: note.text,
          color: note.color,
          position: note.position,
          created_by: note.created_by,
        })),
      );
    };

    loadNotes();

    // Subscribe to changes
    const channel = supabase
      .channel('whiteboard_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'whiteboard_notes' },
        loadNotes,
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [team?.id]);

  const addPostIt = async () => {
    if (!newPostIt.trim() || !team) return;

    const randomPosition = {
      x: Math.random() * 60 - 30,
      y: Math.random() * 60 - 30,
    };

    const { data: note } = await supabase
      .from('whiteboard_notes')
      .insert({
        text: newPostIt,
        color: selectedColor,
        position: randomPosition,
        team_id: team.id,
        created_by: profile.id,
      })
      .select()
      .single();

    if (note) {
      setPostIts([
        ...postIts,
        {
          id: note.id,
          text: note.text,
          color: note.color,
          position: note.position,
          created_by: note.created_by,
        },
      ]);
    }
    setNewPostIt('');
  };

  const removePostIt = async (id: string, created_by: string) => {
    if (created_by !== profile.id) {
      console.error('Je kunt alleen je eigen notities verwijderen');
      return;
    }

    await supabase.from('whiteboard_notes').delete().eq('id', id);

    setPostIts(postIts.filter((postIt) => postIt.id !== id));
  };

  const handleDragStart = (id: string, e: React.DragEvent) => {
    setDraggedPostIt(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedPostIt(null);
  };

  const handleDrag = async (e: React.DragEvent, id: string) => {
    if (!whiteboardRef.current) return;

    const rect = whiteboardRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    if (x >= 0 && x <= 100 && y >= 0 && y <= 100) {
      const newPosition = { x: x - 50, y: y - 50 };

      // Update local state
      setPostIts(
        postIts.map((postIt) =>
          postIt.id === id ? { ...postIt, position: newPosition } : postIt,
        ),
      );

      // Update in database
      await supabase
        .from('whiteboard_notes')
        .update({ position: newPosition })
        .eq('id', id);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto space-y-4">
      <h2 className="text-2xl font-semibold text-center mb-6">Team Spirit</h2>

      <Card className="p-6 bg-white/50 backdrop-blur-sm">
        <h3 className="text-xl font-semibold mb-4">
          Laat een positief TeamSpirit berichtje achter voor je collega's!
        </h3>

        <div className="flex gap-4 mb-6">
          {COLORS.map((color) => (
            <button
              key={color}
              className={`w-16 h-16 rounded-xl shadow-lg transition-transform hover:scale-105 ${color} ${selectedColor === color ? 'ring-4 ring-offset-4 ring-black' : ''}`}
              onClick={() => setSelectedColor(color)}
            />
          ))}
        </div>

        <div className="flex gap-2">
          <Textarea
            value={newPostIt}
            onChange={(e) => setNewPostIt(e.target.value)}
            placeholder="Schrijf een bericht..."
            className={`flex-1 transition-colors ${selectedColor}`}
          />
          <Button
            onClick={addPostIt}
            className="bg-green-500 hover:bg-green-600"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </Card>

      <div
        ref={whiteboardRef}
        className="relative min-h-[600px] rounded-lg border-2 border-gray-200 bg-white"
      >
        {/* Simple grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#e5e7eb_1px,transparent_1px),linear-gradient(to_bottom,#e5e7eb_1px,transparent_1px)] bg-[size:20px_20px]"></div>

        {postIts.map((postIt) => (
          <div
            key={postIt.id}
            draggable
            onDragStart={(e) => handleDragStart(postIt.id, e)}
            onDragEnd={handleDragEnd}
            onDrag={(e) => handleDrag(e, postIt.id)}
            className={`absolute p-4 rounded-lg shadow-lg ${postIt.color} transform cursor-move transition-transform hover:scale-105 ${draggedPostIt === postIt.id ? 'z-50' : 'z-10'}`}
            style={{
              left: `${50 + postIt.position.x}%`,
              top: `${50 + postIt.position.y}%`,
              maxWidth: '200px',
              transform: `rotate(${Math.random() > 0.5 ? '2deg' : '-2deg'})`,
            }}
          >
            {postIt.created_by === profile.id && (
              <button
                onClick={() => removePostIt(postIt.id, postIt.created_by)}
                className="absolute top-1 right-1 p-1 rounded-full hover:bg-black/10"
              >
                <X className="h-3 w-3" />
              </button>
            )}
            <p className="text-sm whitespace-pre-wrap">{postIt.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
